import { stringify, parse } from 'yaml';

import { getETLComponentType } from './etl-component-types/factory';
import { TpETLObjectTypes, TpMatrixColumnType, TpETLTableColumn } from './etl-data-types/interfaces';
import { TpETLComponentTypeManifest, TpETLInputObject, TpETLOutputObject } from './etl-component-types/interfaces';
import {
  TpETLPipeline,
  TpETLPipelineComponent,
  TpETLDryRun,
  TpETLDryRunObject,
  TpETLDryRunTableObject,
  TpETLDryRunMatrixObject,
  isDryRunMatrixObject,
} from './interfaces';

// ******************************* Pipeline ********************

export function getETLComponentById(pipeline: TpETLPipeline, id: string): TpETLPipelineComponent {
  if (!pipeline.componentMap[id]) throw new Error(`Could not find pipeline component ${id}`);
  return pipeline.componentMap[id];
}

// Provided a data object ID, returns information about the component that created it
export function getETLComponentWithOutputObj(
  pipeline: TpETLPipeline,
  objId: string,
): {
  comp: TpETLPipelineComponent;
  outputType: TpETLOutputObject;
} {
  for (const comp of pipeline.components) {
    for (const [outputId, outObjId] of Object.entries(comp.outputObjectIds)) {
      if (outObjId == objId) {
        const compType = getETLComponentType(comp.componentType);
        return {
          comp,
          outputType: getETLComponentTypeOutputInfo(compType, outputId),
        };
      }
    }
  }
  throw new Error(`Object ${objId} not found as output in the pipeline`);
}

// Provided a component ID, returns the list of all components that depend on it
export function getDownstreamDependentComponents(pipeline: TpETLPipeline, compId: string): TpETLPipelineComponent[] {
  const dependentsComps: TpETLPipelineComponent[] = [getETLComponentById(pipeline, compId)];
  let tryComponents = [...pipeline.components];
  let componentAdded = true;
  while (componentAdded) {
    componentAdded = false;
    for (const comp of tryComponents) {
      let compIsDependent = false;
      const dependentObjects = [...Object.values(comp.inputObjectIds), ...comp.extraParamObjectIds];
      for (const depComp of dependentsComps)
        for (const outObjId of Object.values(depComp.outputObjectIds))
          if (dependentObjects.indexOf(outObjId) >= 0) compIsDependent = true;
      if (compIsDependent) {
        dependentsComps.push(comp);
        tryComponents = tryComponents.filter((iterComp) => comp.id !== iterComp.id);
        componentAdded = true;
      }
    }
  }
  dependentsComps.shift(); //first component is the original one, we don't want to return that one
  return dependentsComps;
}

// ******************************* Data Objects *************************

export function getETLComponentInputObject(
  dryRun: TpETLDryRun,
  comp: TpETLPipelineComponent,
  inputId: string,
): TpETLDryRunObject {
  const objId = comp.inputObjectIds[inputId];
  if (!objId) throw `Input not found: ${inputId}`;
  if (!dryRun.objectsMap[objId]) {
    throw `Object not found: ${objId} (input ${inputId} of component ${comp.name})`;
  }
  return dryRun.objectsMap[objId];
}

export function getETLComponentInputObjectTable(
  dryRun: TpETLDryRun,
  comp: TpETLPipelineComponent,
  inputId: string,
): TpETLDryRunTableObject {
  const obj = getETLComponentInputObject(dryRun, comp, inputId);
  if (obj.type !== TpETLObjectTypes.TABLE) throw `Object is not a table (input ${inputId})`;
  return obj as TpETLDryRunTableObject;
}

export function getETLComponentInputObjectMatrix(
  dryRun: TpETLDryRun,
  comp: TpETLPipelineComponent,
  inputId: string,
): TpETLDryRunMatrixObject {
  const obj = getETLComponentInputObject(dryRun, comp, inputId);
  if (!isDryRunMatrixObject(obj)) throw `Object is not a matrix (input ${inputId})`;
  return obj;
}

export function getETLTableObjectColumn(table: TpETLDryRunTableObject, colId: string) {
  for (const col of table.columns) if (col.identifier == colId) return col;
  throw `Column ${colId} not found`;
}

export function getETLTableObjectColumnOptional(table: TpETLDryRunTableObject, colId: string) {
  for (const col of table.columns) if (col.identifier == colId) return col;
  return null;
}

export function getETLTableObjectColumnDataTypeIfPresent(table: TpETLDryRunTableObject, colId: string): string {
  const colInfo = getETLTableObjectColumnOptional(table, colId);
  if (!colInfo) return '';
  return colInfo.dataType;
}

export function getETLComponentInputObjectMatrixColumns(
  dryRun: TpETLDryRun,
  comp: TpETLPipelineComponent,
  inputId: string,
  matrixType: TpMatrixColumnType,
): TpETLTableColumn[] {
  const obj = getETLComponentInputObject(dryRun, comp, inputId);
  if (obj.type !== TpETLObjectTypes.MATRIX) throw `Object is not a table (input ${inputId})`;
  if (matrixType === 'observations') {
    return obj.obsMetadataColumns;
  } else if (matrixType === 'features') {
    return obj.featureMetadataColumns;
  } else {
    throw `Invalid matrix type ${matrixType}`;
  }
}

// ******************************* Component Types ********************

export function getETLComponentTypeInputInfo(compType: TpETLComponentTypeManifest, inputId: string): TpETLInputObject {
  const idx = compType.inputs.map((input) => input.id).indexOf(inputId);
  if (idx < 0) throw `Input ${inputId} not present in component type ${compType.id}`;
  return compType.inputs[idx];
}

export function getETLComponentTypeOutputInfo(
  compType: TpETLComponentTypeManifest,
  outputId: string,
): TpETLOutputObject {
  const idx = compType.outputs.map((output) => output.id).indexOf(outputId);
  if (idx < 0) throw `Output ${outputId} not present in component type ${compType.id}`;
  return compType.outputs[idx];
}

export function exportPipelineToYaml(pipeline: TpETLPipeline): string {
  const obj: any = {
    components: [],
  };
  for (const comp of pipeline.components) {
    obj.components.push({
      id: comp.id,
      componentType: comp.componentType,
      name: comp.name,
      inputs: comp.inputObjectIds,
      extraParams: comp.extraParamObjectIds,
      outputs: comp.outputObjectIds,
      params: comp.params,
    });
  }
  return stringify(obj);
}

export function importPipelineFromYaml(yamlString: string): TpETLPipeline {
  const yaml = parse(yamlString);
  if (!yaml.components) throw 'Components are missing';
  const components: TpETLPipelineComponent[] = yaml.components.map((inpComponent: any) => {
    if (!inpComponent.id) throw 'Component is missing identifier';
    if (!inpComponent.name) throw 'Component is missing name';
    if (!inpComponent.inputs) throw 'Component is missing inputs';
    if (!inpComponent.extraParams) throw 'Component is missing extraParams';
    if (!inpComponent.outputs) throw 'Component is missing outputs';
    if (!inpComponent.params) throw 'Component is missing params';
    const comp = {
      id: inpComponent.id,
      componentType: inpComponent.componentType,
      name: inpComponent.name,
      inputObjectIds: inpComponent.inputs,
      extraParamObjectIds: inpComponent.extraParams,
      outputObjectIds: inpComponent.outputs,
      params: inpComponent.params,
    };
    return comp;
  });
  const componentMap: { [id: string]: TpETLPipelineComponent } = {};
  for (const comp of components) componentMap[comp.id] = comp;
  return {
    components,
    componentMap,
  };
}

export function computeStringHash(obj: object | string): string {
  const getHash = (input: string): string => {
    // Simple non-cryptographic hash function for strings
    let hash = 0;
    for (let i = 0; i < input.length; i++) {
      const char = input.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(16);
  };

  if (typeof obj === 'string') {
    return getHash(obj);
  }

  const sortObject = (data: object): object => {
    if (data === null || typeof data !== 'object') {
      return data; // Return the value if it's not an object
    }
    const sortedKeys = Object.keys(data).sort();
    const sortedObj: Record<string, any> = {};
    for (const key of sortedKeys) {
      sortedObj[key] = sortObject(data[key]); // Recursively sort value
    }
    return sortedObj;
  };

  const sortedObj = sortObject(obj);
  const sortedJsonString = JSON.stringify(sortedObj);

  return getHash(sortedJsonString);
}

// ********************* Dry Run **************************

export function addDryRunObject(dryRun: TpETLDryRun, obj: TpETLDryRunObject) {
  dryRun.objectsMap[obj.id] = obj;
}

export function addDryRunObjectText(
  dryRun: TpETLDryRun,
  objId: string,
  creatingComponent: TpETLPipelineComponent,
): TpETLDryRunObject {
  const obj: TpETLDryRunObject = {
    type: TpETLObjectTypes.TEXT,
    id: objId,
    createdByCompId: creatingComponent.id,
  };
  addDryRunObject(dryRun, obj);
  return obj;
}

export function addDryRunObjectTable(
  dryRun: TpETLDryRun,
  objId: string,
  creatingComponent: TpETLPipelineComponent,
): TpETLDryRunTableObject {
  const obj: TpETLDryRunTableObject = {
    type: TpETLObjectTypes.TABLE,
    id: objId,
    createdByCompId: creatingComponent.id,
    columns: [],
  };
  addDryRunObject(dryRun, obj);
  return obj;
}

export function addDryRunObjectMatrix(
  dryRun: TpETLDryRun,
  objId: string,
  creatingComponent: TpETLPipelineComponent,
): TpETLDryRunMatrixObject {
  const matrix: TpETLDryRunMatrixObject = {
    type: TpETLObjectTypes.MATRIX,
    id: objId,
    createdByCompId: creatingComponent.id,
    obsMetadataColumns: [],
    featureMetadataColumns: [],
    linkColumnId: '',
  };
  addDryRunObject(dryRun, matrix);
  return matrix;
}
